import { useLanguage } from "@/contexts/LanguageContext";
import { ExternalLink } from "lucide-react";
import { Link } from "@/lib/router-compat";

export function Footer() {
  const { t, language } = useLanguage();
  
  const deepinsightsUrl = "https://www.deep-insights.ai/";

  return (
    <footer className="bg-gradient-dark text-primary-foreground py-8">
      <div className="container mx-auto px-4">
        <div className="mb-6 flex flex-col items-center text-center">
          <Link
            to={language === "zh" ? "/zh" : language === "th" ? "/th" : "/"}
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex items-center gap-3 mb-2"
          >
            <svg
              width="48"
              height="48"
              viewBox="0 0 120 120"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="w-10 h-10"
            >
              <path
                d="M 60 20 L 65 50 L 95 25 L 70 55 L 100 60 L 70 65 L 95 95 L 65 70 L 60 100 L 55 70 L 25 95 L 50 65 L 20 60 L 50 55 L 25 25 L 55 50 Z"
                fill="#0BBAB1"
              />
              <path
                d="M 60 32 L 63 52 L 83 37 L 68 57 L 88 60 L 68 63 L 83 83 L 63 68 L 60 88 L 57 68 L 37 83 L 52 63 L 32 60 L 52 57 L 37 37 L 57 52 Z"
                fill="#0BBAB1"
                opacity="0.4"
              />
              <line x1="60" y1="60" x2="60" y2="15" stroke="#0BBAB1" strokeWidth="2.5" strokeLinecap="round" opacity="0.6" />
              <line x1="60" y1="60" x2="95" y2="25" stroke="#0BBAB1" strokeWidth="2.5" strokeLinecap="round" opacity="0.6" />
              <line x1="60" y1="60" x2="105" y2="60" stroke="#0BBAB1" strokeWidth="2.5" strokeLinecap="round" opacity="0.6" />
              <line x1="60" y1="60" x2="95" y2="95" stroke="#0BBAB1" strokeWidth="2.5" strokeLinecap="round" opacity="0.6" />
              <line x1="60" y1="60" x2="60" y2="105" stroke="#0BBAB1" strokeWidth="2.5" strokeLinecap="round" opacity="0.6" />
              <line x1="60" y1="60" x2="25" y2="95" stroke="#0BBAB1" strokeWidth="2.5" strokeLinecap="round" opacity="0.6" />
              <line x1="60" y1="60" x2="15" y2="60" stroke="#0BBAB1" strokeWidth="2.5" strokeLinecap="round" opacity="0.6" />
              <line x1="60" y1="60" x2="25" y2="25" stroke="#0BBAB1" strokeWidth="2.5" strokeLinecap="round" opacity="0.6" />
              <line x1="60" y1="60" x2="78" y2="20" stroke="#0BBAB1" strokeWidth="1.5" strokeLinecap="round" opacity="0.4" />
              <line x1="60" y1="60" x2="100" y2="42" stroke="#0BBAB1" strokeWidth="1.5" strokeLinecap="round" opacity="0.4" />
              <line x1="60" y1="60" x2="100" y2="78" stroke="#0BBAB1" strokeWidth="1.5" strokeLinecap="round" opacity="0.4" />
              <line x1="60" y1="60" x2="78" y2="100" stroke="#0BBAB1" strokeWidth="1.5" strokeLinecap="round" opacity="0.4" />
              <line x1="60" y1="60" x2="42" y2="100" stroke="#0BBAB1" strokeWidth="1.5" strokeLinecap="round" opacity="0.4" />
              <line x1="60" y1="60" x2="20" y2="78" stroke="#0BBAB1" strokeWidth="1.5" strokeLinecap="round" opacity="0.4" />
              <line x1="60" y1="60" x2="20" y2="42" stroke="#0BBAB1" strokeWidth="1.5" strokeLinecap="round" opacity="0.4" />
              <line x1="60" y1="60" x2="42" y2="20" stroke="#0BBAB1" strokeWidth="1.5" strokeLinecap="round" opacity="0.4" />
              <circle cx="60" cy="60" r="16" fill="#0BBAB1" />
              <path
                d="M 53 67 L 53 53 L 55 53 L 65 63 L 65 53 L 67 53 L 67 67 L 65 67 L 55 57 L 55 67 Z"
                fill="white"
                opacity="0.9"
              />
              <circle cx="60" cy="60" r="12" fill="none" stroke="white" strokeWidth="1" opacity="0.3" />
              <circle cx="60" cy="56" r="4" fill="white" opacity="0.5" />
            </svg>
            <span className="text-xl font-bold">
              Cargo<span className="text-secondary">Nova</span>
            </span>
          </Link>

          <p className="text-primary-foreground/70 text-sm leading-relaxed">{t("footer.slogan")}</p>
        </div>

        <div className="pt-8 border-t border-primary-foreground/10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
            <div className="flex flex-col gap-1 md:justify-self-start text-center md:text-left">
              <p className="text-sm text-primary-foreground/50">{t("footer.copyright")}</p>
              <p className="text-xs text-primary-foreground/30">Last updated: June 26, 2026</p>
            </div>
            <div className="flex flex-wrap items-center justify-center md:justify-end gap-x-6 gap-y-2 text-sm text-primary-foreground/50">
              <Link
                to={language === "zh" ? "/zh" : language === "th" ? "/th" : "/"}
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                className="hover:text-secondary transition-colors"
              >
                CargoNova
              </Link>
              <Link
                to={language === "zh" ? "/zh/privacy" : language === "th" ? "/th/privacy" : "/privacy"}
                className="hover:text-secondary transition-colors"
              >
                {t("footer.privacy")}
              </Link>
              <a
                href="https://www.linkedin.com/company/deep-insights-technology/?viewAsMember=true"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-secondary transition-colors inline-flex items-center gap-1"
              >
                LinkedIn <ExternalLink className="w-3 h-3" />
              </a>
              <a
                href="https://www.deep-insights.ai/resources/blog"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-secondary transition-colors inline-flex items-center gap-1"
              >
                Blog <ExternalLink className="w-3 h-3" />
              </a>
              <a
                href="https://www.deep-insights.ai/trust-security"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-secondary transition-colors inline-flex items-center gap-1"
              >
                Trust & Security <ExternalLink className="w-3 h-3" />
              </a>
              <a
                href={deepinsightsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-secondary transition-colors"
              >
                {t("footer.deepInsightsTagline")}
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
